/* Conta e dados: quem está conectado, troca de senha, histórico do legado por empresa
   e, para integrações, URL do backend, API key e ano padrão. */

import { api } from '../core/api';
import { carregaCatalogo, esquecerCatalogo } from '../core/catalogo';
import { BACKEND_PADRAO, CFG, isApi, salvarCfg } from '../core/config';
import { state } from '../core/estado';
import { esc } from '../core/formato';
import { definirTema, trocarSenha } from '../core/sessao';
import type { Catalogo } from '../tipos/api';
import { erroBox, loading } from '../ui/componentes';
import { $, ic, valorDe } from '../ui/dom';
import { renderEnv, renderQuem } from '../ui/shell';
import { toast } from '../ui/toast';
import { render } from '../roteador';
import type { Pagina } from './pagina';

const cnpjTela = (c: string) => c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');

function legadoHtml(c: Catalogo): string {
  const empresas = c.empresas?.length ? c.empresas : [{ cnpj: c.empresa.cnpj, nome: c.empresa.nome, usar_legado: c.empresa.usar_legado }];
  return empresas.map((e) => {
    const ligado = e.usar_legado !== false;
    return `<div class="map-row">
      <div><b>${esc(e.nome)}</b><small>CNPJ ${esc(cnpjTela(e.cnpj))} · ${ligado ? 'mostra o histórico do legado' : 'mostra só os lançamentos da plataforma nova'}</small></div>
      <label class="switch"><input type="checkbox" data-muda="legado" data-cnpj="${esc(e.cnpj)}" ${ligado ? 'checked' : ''}>
        <i></i><span>${ligado ? 'Legado ligado' : 'Legado desligado'}</span></label></div>`;
  }).join('');
}

function contaHtml(): string {
  if (!isApi()) {
    return `<div class="card"><h2 class="sec">Conta</h2>
      <p style="margin:0 0 14px;color:var(--muted)">Você está na demonstração, com dados simulados.</p>
      <button class="btn btn-primary" data-clique="sairDemo">${ic('i-logout')} Entrar com e-mail e senha</button></div>`;
  }
  if (!CFG.token) {
    return `<div class="card"><h2 class="sec">Conta</h2>
      <p style="margin:0;color:var(--muted)">Conectado por API key (integração). A troca de senha é feita com login.</p></div>`;
  }
  return `<div class="card"><h2 class="sec">Conta</h2>
    <div class="map-row" style="margin:0 0 18px;border-bottom:1px solid var(--line-2);padding:0 0 16px">
      <div><b>${esc(CFG.usuario?.nome || '')}</b><small>${esc(CFG.usuario?.email || '')}</small></div>
      <label class="switch"><input type="checkbox" data-muda="tema" ${CFG.usuario?.preferencias?.tema === 'escuro' ? 'checked' : ''}><i></i><span>Tema escuro</span></label></div>
    <h3 style="font-size:14px;margin:0 0 10px">Trocar senha</h3>
    <div class="grid g2" style="gap:0 14px">
      <div class="field"><label for="senhaAtual">Senha atual</label><input class="inp" id="senhaAtual" type="password" autocomplete="current-password"></div>
      <div class="field"><label for="senhaNova">Nova senha</label><input class="inp" id="senhaNova" type="password" autocomplete="new-password">
        <div class="hint">Pelo menos 8 caracteres, com letras e números.</div></div>
    </div>
    <div class="field"><label for="senhaConfirma">Repita a nova senha</label><input class="inp" id="senhaConfirma" type="password" autocomplete="new-password" style="max-width:300px"></div>
    <button class="btn btn-ghost" data-clique="senha">${ic('i-lock')} Trocar senha</button>
    <p style="font-size:12.5px;color:var(--muted);margin:12px 0 0">As outras sessões abertas com a senha antiga são encerradas.</p></div>`;
}

export const config: Pagina = {
  semCatalogo: true,

  render: () => `
  <h1 class="page">Conta e dados</h1>
  <p class="sub">Sua conta, de onde vêm os dados de cada empresa e a conexão com o backend.</p>
  <div style="max-width:720px;display:flex;flex-direction:column;gap:18px">
    ${contaHtml()}
    ${isApi() ? `<div class="card"><h2 class="sec">Histórico do legado</h2>
      <p style="margin:0 0 6px;color:var(--muted);font-size:13.5px">Ligado, a plataforma mostra o histórico já processado pela ZNIT no sistema anterior. Desligado, mostra só o que foi lançado na plataforma nova para aquele CNPJ. A escolha vale para a sua conta.</p>
      <div id="cfgLegado">${loading('Carregando empresas…')}</div></div>` : ''}
    <details class="card adv" ${CFG.token || !isApi() ? '' : 'open'}><summary>Conexão (avançado)</summary>
      <div class="field"><label for="cfgBase">URL do backend</label>
        <input class="inp" id="cfgBase" value="${esc(CFG.base)}" placeholder="${BACKEND_PADRAO}"></div>
      <div class="grid g2" style="gap:0 14px">
        <div class="field"><label for="cfgKey">API key (integrações)</label>
          <input class="inp" id="cfgKey" type="password" value="${esc(CFG.apiKey)}" placeholder="sk_…" autocomplete="off">
          <div class="hint">Só é usada sem login. Com login, vale a sessão.</div></div>
        <div class="field"><label for="cfgAno">Ano padrão</label>
          <input class="inp" id="cfgAno" value="${esc(CFG.ano)}" inputmode="numeric"></div>
      </div>
      <button class="btn btn-primary" data-clique="salvar">${ic('i-save')} Salvar</button>
    </details>
  </div>`,

  async carregar(tk) {
    if (!isApi()) return;
    try {
      const c = await carregaCatalogo();
      if (tk === state.tk) $('#cfgLegado').innerHTML = legadoHtml(c);
    } catch (e) {
      if (tk === state.tk) $('#cfgLegado').innerHTML = erroBox(e);
    }
  },

  acoes: {
    salvar() {
      CFG.base = valorDe('cfgBase').replace(/\/+$/, '').replace(/\/api\/v1(\/wl|\/znit)?$/, '') || BACKEND_PADRAO;
      const chave = valorDe('cfgKey');
      if (chave !== CFG.apiKey) CFG.cnpj = ''; // outra chave, outras empresas
      CFG.apiKey = chave;
      const ano = parseInt(valorDe('cfgAno'), 10);
      if (ano > 1990 && ano < 2100) CFG.ano = ano;
      salvarCfg();
      state.f.ano = CFG.ano;
      state.f.obra = '';
      esquecerCatalogo();
      renderEnv();
      renderQuem();
      render();
      toast('Conexão salva.');
    },

    async senha(el) {
      const atual = valorDe('senhaAtual');
      const nova = valorDe('senhaNova');
      if (!atual || !nova) return toast('Preencha a senha atual e a nova.');
      if (nova !== valorDe('senhaConfirma')) return toast('A confirmação não bate com a nova senha.');
      el.classList.add('dis');
      try {
        await trocarSenha(atual, nova);
        toast('Senha trocada.');
        render();
      } catch (e) {
        toast((e as Error).message);
        el.classList.remove('dis');
      }
    },

    async legado(el) {
      const input = el as HTMLInputElement;
      input.disabled = true;
      try {
        await api('PUT', `/empresas/${el.dataset.cnpj}/fonte`, { body: { usar_legado: input.checked } });
        esquecerCatalogo();
        toast(input.checked ? 'Histórico do legado ligado.' : 'Legado desligado: só os dados da plataforma nova.');
        render();
      } catch (e) {
        input.checked = !input.checked;
        input.disabled = false;
        toast((e as Error).message);
      }
    },

    async tema(el) {
      const input = el as HTMLInputElement;
      input.disabled = true;
      try {
        await definirTema(input.checked ? 'escuro' : 'claro');
        toast(input.checked ? 'Tema escuro ligado nesta conta.' : 'Tema claro nesta conta.');
      } catch (e) {
        input.checked = !input.checked;
        toast((e as Error).message);
      }
      input.disabled = false;
    },

    sairDemo() {
      document.getElementById('btnSair')?.click();
    },
  },
};
